import React from "react";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";

export interface HelpItem {
  /** Short name of a term/element on the page (e.g. "Status", "Agent"). */
  term: string;
  /** One-line explanation of what it is / what it does. */
  desc: React.ReactNode;
}

interface TitleProps {
  children?: React.ReactNode;
  /** Optional short paragraph describing what this page is for. */
  help?: React.ReactNode;
  /** Optional "what's what" glossary shown under the description. */
  helpItems?: HelpItem[];
}

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 800,
  letterSpacing: 0,
  lineHeight: 1.2,
}));

export default function Title({ children, help, helpItems }: TitleProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const hasHelp = Boolean(help || (helpItems && helpItems.length));

  const titleNode = (
    <StyledTypography
      component="h1"
      variant="h5"
      sx={{ fontSize: { xs: 20, sm: 24 } }}
      gutterBottom={!hasHelp}
    >
      {children}
    </StyledTypography>
  );

  if (!hasHelp) return titleNode;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        mb: { xs: 1.5, sm: 2 },
      }}
    >
      {titleNode}
      <IconButton
        size="small"
        aria-label="What is this page?"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: "#98a2b3", "&:hover": { color: "primary.main" } }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 380,
              p: 2,
              border: "1px solid var(--app-border, #e4e7ec)",
              boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
            },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: "text.secondary",
            mb: 0.75,
          }}
        >
          About this page
        </Typography>

        {help && (
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, color: "text.primary" }}>
            {help}
          </Typography>
        )}

        {helpItems && helpItems.length > 0 && (
          <Box
            component="dl"
            sx={{ m: 0, mt: help ? 1.5 : 0, display: "grid", rowGap: 1.1 }}
          >
            {helpItems.map((item) => (
              <Box key={item.term}>
                <Typography
                  component="dt"
                  sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}
                >
                  {item.term}
                </Typography>
                <Typography
                  component="dd"
                  sx={{ m: 0, fontSize: 12.5, lineHeight: 1.5, color: "text.secondary" }}
                >
                  {item.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Popover>
    </Box>
  );
}
